import { Box, Typography, Button, useTheme } from '@mui/material';
import { InfoIcon } from '../Icons/Icons';

import { CloseOutlined, Warning, Error, OpenInNew } from '@mui/icons-material';

import { useBannerStore } from '@/stores/bannerStore';
import { useNavigate } from 'react-router-dom';
import { RouteMap } from '@/routes/Routes';
import { useState } from 'react';
import BannerContentModal from './BannerContentModal';
import { useAuthStore } from '@/stores/authStore';
import { getTrialStatus } from '@/helpers/trialStatus';
import GoldButton from '../GoldButton/GoldButton';

const DynamicBanner = () => {
  const { message, closeBanner } = useBannerStore();
  const navigate = useNavigate();
  const theme = useTheme();
  const userData = useAuthStore((state) => state.userData);

  const [modalOpen, setModalOpen] = useState(false);

  const handleClose = () => {
    closeBanner();
  };

  const handleReadMore = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  if (!message) {
    return <></>;
  }

  const isUpgradeMessage = message.id === 'upgrade-message';
  const isTrialBanner = message.id === 'trial-banner';
  const isGracePeriodBanner = message.id === 'grace-period-banner';

  // Check if user is actually on trial (not just showing trial banner)
  const trialStatus = getTrialStatus(userData);
  const isUserOnTrial = trialStatus.isOnTrial;

  // Default to 'info' if no severity is specified
  const severity = message.severity || 'info';

  // Get colors and icons based on severity
  const getSeverityConfig = (severity: 'info' | 'warning' | 'error') => {
    switch (severity) {
      case 'warning':
        return {
          backgroundColor: 'rgba(255, 243, 224, 1)', // Light orange background
          textColor: theme.palette.warning.dark,
          icon: <Warning sx={{ color: theme.palette.warning.main }} />,
        };
      case 'error':
        return {
          backgroundColor: 'rgba(255, 235, 238, 1)', // Light red background
          textColor: theme.palette.error.dark,
          icon: <Error sx={{ color: theme.palette.error.main }} />,
        };
      case 'info':
      default:
        return {
          backgroundColor: 'rgba(229, 246, 253, 1)', // Current blue background
          textColor: theme.palette.info.dark,
          icon: <InfoIcon />,
        };
    }
  };

  const config = getSeverityConfig(severity);

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: { xs: '48px', sm: '52px' },
        background: config.backgroundColor,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: { xs: '8px 12px', sm: '0 16px' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          flex: 1,
        }}
      >
        {config.icon}
        <Typography
          sx={{
            color: config.textColor,
            fontSize: { xs: '0.875rem', sm: '1rem' },
            fontWeight: 500,
            mr: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          {message.message}
        </Typography>

        {(isUpgradeMessage || isTrialBanner) && (
          <>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => navigate(`${RouteMap.PLAN}#${RouteMap.PLAN_ANCHOR}`)}
              sx={{
                ml: { xs: 0.5, sm: 1 },
                minWidth: 'auto',
                px: { xs: 1.5, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                textTransform: 'none',
              }}
            >
              Upgrade
            </Button>
            {isUserOnTrial && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => navigate(RouteMap.TRIAL_WELCOME)}
                sx={{
                  ml: { xs: 0.5, sm: 1 },
                  minWidth: 'auto',
                  px: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  textTransform: 'none',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                Details
              </Button>
            )}
          </>
        )}

        {isGracePeriodBanner && (
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => navigate(`${RouteMap.PLAN}#${RouteMap.PLAN_ANCHOR}`)}
            sx={{
              ml: { xs: 0.5, sm: 1 },
              minWidth: 'auto',
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              textTransform: 'none',
              backgroundColor: theme.palette.error.main,
              '&:hover': {
                backgroundColor: theme.palette.error.dark,
              },
            }}
          >
            Update Payment
          </Button>
        )}

        {message.button?.text &&
          message.button?.url &&
          (() => {
            const btn = message.button as { text: string; url: string; style?: string };
            const style = btn.style || 'primary';
            const commonSx = {
              ml: { xs: 0.5, sm: 1 },
              mr: { xs: 0.5, sm: 1 },
              minWidth: 'auto',
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              textTransform: 'none' as const,
              flexShrink: 0,
              whiteSpace: 'nowrap' as const,
            };
            const icon = <OpenInNew sx={{ fontSize: '16px' }} />;
            if (style === 'gold') {
              return (
                <GoldButton size="small" sx={commonSx} startIcon={icon} href={btn.url}>
                  {btn.text}
                </GoldButton>
              );
            }
            const variant = style === 'outlined' ? 'outlined' : 'contained';
            const color = style === 'error' ? 'error' : 'primary';
            return (
              <Button variant={variant} color={color} size="small" sx={commonSx} startIcon={icon} href={btn.url}>
                {btn.text}
              </Button>
            );
          })()}

        {message.content && !message.button?.text && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleReadMore}
            startIcon={<OpenInNew sx={{ fontSize: '16px' }} />}
            sx={{
              ml: { xs: 0.5, sm: 1.5 },
              color: config.textColor,
              borderColor: config.textColor,
              backgroundColor: 'transparent',
              borderRadius: '16px',
              padding: { xs: '2px 8px', sm: '2px 12px' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              fontWeight: 500,
              textTransform: 'none',
              minHeight: 'auto',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              display: { xs: 'none', sm: 'flex' }, // Hide on mobile to save space
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderColor: config.textColor,
              },
            }}
          >
            Read more
          </Button>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: { xs: '6px', sm: '10px' },
          alignItems: 'center',
          ml: 'auto',
        }}
      >
        <CloseOutlined
          sx={{
            color: config.textColor,
            width: { xs: '18px', sm: '20px' },
            ml: 1,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.7,
            },
          }}
          onClick={handleClose}
        />
      </Box>
      {message.content && (
        <BannerContentModal
          open={modalOpen}
          onClose={handleModalClose}
          title="Message Details"
          content={message.content}
        />
      )}
    </Box>
  );
};

export default DynamicBanner;
